import React, { useState } from 'react';
import { useField } from 'formik';
import { Check, ChevronDown } from 'lucide-react';
import {
  Select as ShadSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@dashboard/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@dashboard/components/ui/command';
import { Button } from '@dashboard/components/ui/button';
import { cn } from '@dashboard/lib/utils';

const EMPTY_VALUE_SENTINEL = '__cp_empty_value__';
const toSentinel = v => (v === '' || v === undefined || v === null ? EMPTY_VALUE_SENTINEL : v);
const fromSentinel = v => (v === EMPTY_VALUE_SENTINEL ? '' : v);

function SingleSelect({ name, value, onChange, options, placeholder, disabled }) {
  const isControlled = value !== undefined;
  const selectProps = isControlled ? { value: toSentinel(value) } : {};

  const handleValueChange = nextValue => {
    if (!onChange) return;
    onChange(fromSentinel(nextValue));
  };

  return (
    <ShadSelect {...selectProps} onValueChange={handleValueChange} disabled={disabled} name={name}>
      <SelectTrigger id={name} className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(option => (
          <SelectItem
            key={option.value}
            value={toSentinel(option.value)}
            disabled={option.disabled}
          >
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </ShadSelect>
  );
}

function MultiSelect({
  name,
  value = [],
  onChange,
  options,
  placeholder,
  disabled,
  summary,
  selectAll,
  selectAllLabel,
  emptyStateTitle,
  emptyStateDescription,
}) {
  const [open, setOpen] = useState(false);
  const hasOptions = options.length > 0;
  const selected = Array.isArray(value) ? value : [];
  const visibleSelected = selected.filter(v => options.some(o => o.value === v));
  const allSelected = visibleSelected.length === options.length && hasOptions;

  const toggleValue = optionValue => {
    if (selected.includes(optionValue)) {
      onChange(selected.filter(v => v !== optionValue));
    } else {
      onChange([...selected, optionValue]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={name}
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {visibleSelected.length === 0 ? (
            <span className="min-w-0 flex-1 truncate text-left text-muted-foreground">
              {placeholder}
            </span>
          ) : summary ? (
            <span className="min-w-0 flex-1 truncate text-left">
              {visibleSelected
                .map(v => options.find(o => o.value === v)?.name)
                .filter(Boolean)
                .join(', ')}
            </span>
          ) : (
            <span className="-ml-1 flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
              {visibleSelected.map(v => {
                const opt = options.find(o => o.value === v);
                return (
                  <span
                    key={v}
                    className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs whitespace-nowrap"
                  >
                    {opt?.name}
                  </span>
                );
              })}
            </span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
        <Command>
          {!hasOptions && emptyStateTitle ? (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-muted-foreground">
              <p className="font-medium text-gray-700 dark:text-muted-foreground">
                {emptyStateTitle}
              </p>
              {emptyStateDescription && <p className="mt-1">{emptyStateDescription}</p>}
            </div>
          ) : (
            <CommandList>
              <CommandEmpty>No options.</CommandEmpty>
              <CommandGroup>
                {selectAll && hasOptions && (
                  <CommandItem
                    value="__cp_select_all__"
                    onSelect={() => {
                      if (allSelected) onChange([]);
                      else onChange(options.map(o => o.value));
                    }}
                  >
                    <Check className={cn('h-4 w-4', allSelected ? 'opacity-100' : 'opacity-0')} />
                    {selectAllLabel} ({options.length})
                  </CommandItem>
                )}
                {options.map(option => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    onSelect={() => {
                      if (option.disabled) return;
                      toggleValue(option.value);
                    }}
                  >
                    <Check
                      className={cn(
                        'h-4 w-4',
                        selected.includes(option.value) ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {option.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ListboxField(props) {
  const [field, , helpers] = useField(props);

  const handleChange = next => {
    helpers.setValue(next);
    if (props.onChange) props.onChange(next);
  };

  if (props.multiple) {
    return (
      <MultiSelect
        name={field.name}
        value={field.value}
        onChange={handleChange}
        options={props.options ?? []}
        placeholder={props.placeholder ?? 'Select an option'}
        disabled={props.disabled}
        summary={props.summary}
        selectAll={props.selectAll}
        selectAllLabel={props.selectAllLabel ?? 'Select all'}
        emptyStateTitle={props.emptyStateTitle}
        emptyStateDescription={props.emptyStateDescription}
      />
    );
  }

  return (
    <SingleSelect
      name={field.name}
      value={field.value}
      onChange={handleChange}
      options={props.options ?? []}
      placeholder={props.placeholder ?? 'Select an option'}
      disabled={props.disabled}
    />
  );
}
